import { get } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Table, Divider, Button } from "semantic-ui-react";
import { FieldArray } from "formik";

import * as Yup from "yup";
import * as Schemas from "../forms/validationSchemas.js";

import ConnectionRow from "./ConnectionRow.jsx";

import { ROW_STATE } from "../forms/formConstants.js";
import { OPERATIONS } from "../../constants/operations.js";

import { operationAvailable } from "../../utils/operations.js";
import { getDisabledStateFromMarking } from "../../utils/marking.js";
import operationSelector from "../../state/reducers/operations/operationSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class ConnectionsPanel extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    showAdd: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    apiSuccesses: PropTypes.object,
    apiErrors: PropTypes.object,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    showAdd: false,
    readOnly: false,
    disabled: false
  };

  constructor(props) {
    super(props);

    const connections = get(props, "formikProps.values.connections", null);

    this.initialValues = {
      details: {
        name: "",
        description: ""
      },
      connections: connections || []
    };

    this.state = {
      newCount: connections.length || 0
    };
  }

  componentDidUpdate(prevProps) {
    const { apiSuccesses, formikProps } = this.props;
    if (apiSuccesses && apiSuccesses !== prevProps.apiSuccesses) {
      Object.keys(apiSuccesses).forEach(key => {
        const result = apiSuccesses[key].result;
        if (result["@id"]) {
          formikProps.setFieldValue(`${key}`, apiSuccesses[key].result);
        }
        formikProps.setFieldValue(`${key}.state`, ROW_STATE.VIEW);
      });
    }
  }

  // -- DATA VALIDATION -- //

  static validationScheme = function (messages) {
    return Yup.array().of(
      Yup.object({
        state: Yup.string(),
        value: Yup.string().when("state", {
          is: value => value === ROW_STATE.NEW,
          then: Schemas.cidrOrSecurityGroupSchema("IP address", messages, true),
          otherwise: Yup.string()
        }),
        description: Yup.string().when("state", {
          is: value => value === ROW_STATE.DELETE,
          then: Yup.string(),
          otherwise: Schemas.simpleLongStringSchema(
            "Description",
            messages,
            true
          )
        }),
        scope: Yup.string().when("state", {
          is: value => value === ROW_STATE.NEW,
          then: Schemas.simpleStringSchema("Scope", messages, true),
          otherwise: Yup.string()
        })
      })
    );
  };

  // -- HANDLERS -- //

  addConnection(push) {
    const { newCount } = this.state;

    const id = `connections[${newCount}]`;
    const connection = {
      id,
      value: "",
      description: "",
      scope: "",
      state: ROW_STATE.NEW
    };

    this.setState({ newCount: newCount + 1 });

    push(connection);
  }

  editConnection = id => {
    this.props.formikProps.setFieldValue(`${id}.state`, ROW_STATE.EDIT);
  };

  deleteConnection = id => {
    this.props.formikProps.setFieldValue(`${id}.state`, ROW_STATE.DELETE);
  };

  resetConnection = id => {
    const { formikProps } = this.props;

    formikProps.setFieldValue(
      `${id}.value`,
      get(formikProps, `initialValues.${id}.value`, "")
    );
    formikProps.setFieldValue(
      `${id}.description`,
      get(formikProps, `initialValues.${id}.description`, "")
    );
    formikProps.setFieldValue(
      `${id}.scope`,
      get(formikProps, `initialValues.${id}.scope`, "")
    );
    formikProps.setFieldValue(`${id}.state`, ROW_STATE.VIEW);
  };

  // -- RENDER -- //

  renderConnectionRows = (connections, remove) => {
    const {
      id,
      data,
      permissions,
      disabled,
      formikProps,
      apiSuccesses,
      apiErrors,
      copyKey
    } = this.props;

    if (!connections) return null;

    return connections.map((connection, index) => {
      const connectionId = `${id}[${index}]`;
      const rowData = data.find(item => item.id === connection.id);
      const disabledState = getDisabledStateFromMarking(connection.marking);

      const mode = get(
        formikProps,
        `values.${connectionId}.state`,
        ROW_STATE.VIEW
      );

      const editAvailability = operationAvailable(
        OPERATIONS.UPDATE,
        connection.operations,
        connection.enabledOperations,
        permissions
      );

      const isEditActive =
        connection.state === ROW_STATE.NEW ? true : editAvailability.available;

      const deleteAvailability = operationAvailable(
        OPERATIONS.DELETE,
        connection.operations,
        connection.enabledOperations,
        permissions
      );

      const isDeleteActive =
        deleteAvailability.available && !disabledState.functions;

      const onEdit = isEditActive ? this.editConnection : null;
      const onDelete = isDeleteActive ? this.deleteConnection : null;

      return (
        <ConnectionRow
          id={connectionId}
          key={connectionId}
          data={rowData}
          formikProps={formikProps}
          mode={mode}
          onEdit={onEdit}
          onDelete={onDelete}
          onRemove={() => remove(index)}
          onReset={this.resetConnection}
          readOnly={!isEditActive}
          disabled={disabled || !isEditActive}
          apiSuccesses={apiSuccesses}
          apiErrors={apiErrors}
          copyKey={copyKey}
        />
      );
    });
  };

  render() {
    const { id, formikProps, showAdd, disabled, copyKey, copy } = this.props;

    // if is no id array in the values we aren't even permitted to view the connections
    if (!get(formikProps, `values.${id}`, null)) {
      return null;
    }

    return (
      <Container className="clearfix">
        <p>{copy(`${copyKey}.body`)}</p>
        <FieldArray
          name={id}
          render={({ push, remove, form: { values } }) => {
            const instances = values[id];
            const hasInstances = instances && instances.length > 0;

            return (
              <Fragment>
                {!hasInstances && (
                  <Fragment>
                    <Divider />
                    <p>{copy(`${copyKey}.no_connections`)}</p>
                  </Fragment>
                )}
                {hasInstances && (
                  <Table basic striped unstackable className="clear-both">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>
                          {copy(`${copyKey}.value`)}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                          {copy(`${copyKey}.description`)}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                          {copy(`${copyKey}.scope`)}
                        </Table.HeaderCell>
                        <Table.HeaderCell collapsing />
                        <Table.HeaderCell collapsing />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {this.renderConnectionRows(instances, remove)}
                    </Table.Body>
                  </Table>
                )}
                {showAdd && (
                  <Button
                    onClick={() => this.addConnection(push, instances.length)}
                    content={copy(`${copyKey}.add_cta`)}
                    icon="plus circle"
                    className="add-resource-cta"
                    type="button"
                  />
                )}
              </Fragment>
            );
          }}
        />
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  permissions: operationSelector.permissionsQuery(state),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ConnectionsPanel);
